import { useEffect } from 'react'
import { PressableProps } from 'react-native'

interface Props {
  action: PressableProps['onPress']
  disabled?: boolean
}

const useOnEnterKeyPress = ({ action, disabled }: Props) => {
  useEffect(() => {
    if (!action) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // If the button is not 'Enter', we don't care.
      if (e.key !== 'Enter') return

      // If the hook is disabled (i.e., if the form is in the process of submitting, or there's a validation error),
      // we should not allow submission with 'Enter'.
      if (disabled) return

      // If the user holds the 'Enter' key down, we should prevent calling the `action` multiple times.
      if (e.repeat) return

      // Skip when the key was aimed at a control that handles 'Enter' itself,
      // otherwise the keystroke runs twice: once as that control's own action and
      // once as this screen's primary action.
      //
      // Checking the tag name is not enough. react-native-web renders every
      // <Pressable> as a plain `div` carrying `tabindex="0"` plus its own keydown
      // handler that fires `onPress` on 'Enter' (see PressResponder.js) — so for a
      // keyboard or screen-reader user, activating ANY button in the app used to
      // also fire the screen's primary button (e.g. Send / Sign) while
      // preventDefault() swallowed the key they actually meant to press.
      // `target` is an Element whenever focus sits in the document, but not when
      // the event is retargeted to `window`/`document` — guard rather than throw
      // out of a listener that runs on every Enter keypress in the app.
      const target = e.target as HTMLElement | null
      if (!target || typeof target.matches !== 'function') return

      const isInteractive =
        ['BUTTON', 'INPUT', 'TEXTAREA', 'SELECT', 'A'].includes(target.tagName) ||
        target.isContentEditable ||
        // A focusable widget: RNW Pressables, and anything given an explicit role.
        target.matches(
          '[tabindex]:not([tabindex="-1"]), [role="button"], [role="link"], [role="checkbox"], [role="switch"], [role="radio"], [role="combobox"], [role="menuitem"], [role="tab"], [role="option"]'
        )

      if (isInteractive) return

      e.preventDefault()
      e.stopPropagation()

      // @ts-ignore
      action()
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [disabled, action])
}

export default useOnEnterKeyPress
